import React, { useEffect, useMemo, useState } from "react";
import "./style.css";
import { Select, message } from "antd";
import { getFuel } from "../../../API/fuel";
import RouteIcon from "@mui/icons-material/Route";
import Lottie from "lottie-react";
import reportGeneration from "../../../Assets/Animations/Report Generation.json";
import { useDateFilter } from "../../../Contexts/DateFilterContext";
import LineChart from "../../../Components/Charts/LineChart";
import BarChart from "../../../Components/Charts/BarChart";
import DynamicBarChart from "../../../Components/Charts/DynamicBarChart";
import GenericTable from "../../../Components/GenericTable";
import CurrentFuelLevels from "./CurrentFuelLevels";
import Map from "./Map";
import { useSwitch } from "../../../Contexts/SwitchContext";
import MapComponent from "./Map/MapComponent";

const { Option } = Select;

const defaultFuelData = {
  total_fuel_tanks: 0,
  total_drains: 0,
  total_fillings: 0,
  filling_volume: 0,
  drain_volume: 0,
  fuel_economy: "",
  current_fuel_level: [],
  last_volume_stats: {},
  quantity_of_fuel: {},
  fuel_use_trend: {},
  fuel_economy_data: {},
  good_fuel_economy: 0,
  mild_fuel_economy: 0,
  high_fuel_economy: 0,
  fuel_removal_by_location: [],
  fuel_removal_time: {},
};

const Fuel = () => {
  const { from, to } = useDateFilter();
  const { switchValue } = useSwitch();
  const [fuelData, setFuelData] = useState(defaultFuelData);
  const [carName, setCarName] = useState("Not Selected");
  const [loading, setLoading] = useState(true);
  const [mapCoordinates, setMapCoordinates] = useState({
    latitude: 18.405074,
    longitude: -78.260424,
  });
  const [loadingMap, setLoadingMap] = useState(false);
  const [keys, setKeys] = useState("");
  const [selectedKey, setSelectedKey] = useState(null);
  const [percentages, setPercentages] = useState({});

  const handleSeeLocation = async (location, name) => {
    console.log("Location", location, "Name", name);
    setLoadingMap(true);
    const [latitude, longitude] = location.split(", ").map(Number);
    await setMapCoordinates({ latitude, longitude });
    await setCarName(name);
    console.log(carName, mapCoordinates, loadingMap);
    setLoadingMap(false);
  };

  const columnsRemove = [
    {
      title: <span className="vehicle-title">Vehicle Name</span>,
      dataIndex: "vehicle_name",
      key: "vehicle_name",
      sorter: (a, b) =>
        a.vehicle_name.localeCompare(b.vehicle_name, undefined, {
          numeric: true,
        }),
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      render: (text, record) => (
        <div
          onClick={() =>
            handleSeeLocation(record.location, record.vehicle_name)
          }
        >
          <div
            className="see-location"
            style={{
              display: "flex",
              alignItems: "center",
              color: "blue",
              cursor: "pointer",
            }}
          >
            <RouteIcon style={{ marginRight: 8 }} /> See Location
          </div>
        </div>
      ),
    },
    {
      title: "Fuel",
      dataIndex: "fuel",
      key: "fuel",
      sorter: (a, b) =>
        a.fuel.localeCompare(b.fuel, undefined, {
          numeric: true,
        }),
      render: (text) => (
        <span style={{ color: "red", fontWeight: "bold" }}>{text} L</span>
      ),
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      sorter: (a, b) =>
        a.date.localeCompare(b.date, undefined, {
          numeric: true,
        }),
    },
    {
      title: "Time",
      dataIndex: "time",
      key: "time",
      sorter: (a, b) =>
        a.time.localeCompare(b.time, undefined, {
          numeric: true,
        }),
    },
  ];
  useEffect(() => {
    if (!to || !from) {
      return;
    }

    const fetchFuelData = async () => {
      try {
        setLoading(true);
        const response = await getFuel(to, from, switchValue);
        await setFuelData(response.data);
        const trendKeys = Object.keys(response.data?.fuel_use_trend || {});
        await setKeys(trendKeys);

        // default to first key if exists
        if (trendKeys.length > 0) {
          setSelectedKey(trendKeys[0]);
        }

        let arr = [];

        for (let values of response.data?.current_fuel_level || []) {
          if (values?.vehicle_name) {
            arr.push(values.vehicle_name);
          }
        }

        const values = (fuelData?.fuel_economy_data?.fuel || [])
          .map(Number)
          .filter((v) => Number.isFinite(v))
          .sort((a, b) => a - b);

        const percentile = (vals, p) => {
          if (!vals.length) return 0;

          const idx = (p / 100) * (vals.length - 1);
          const lower = Math.floor(idx);
          const upper = Math.ceil(idx);
          const weight = idx - lower;

          return vals[lower] * (1 - weight) + vals[upper] * weight;
        };

        const q1 = percentile(values, 25);
        const q3 = percentile(values, 75);

        const counts = values.reduce(
          (acc, v) => {
            if (v < q1) acc.low++;
            else if (v > q3) acc.good++;
            else acc.mild++;
            return acc;
          },
          { low: 0, mild: 0, good: 0 }
        );

        if (values.length) {
          const total = values.length || 1;

          const percentagesCalc = {
            low: +((counts.low / total) * 100).toFixed(0),
            mild: +((counts.mild / total) * 100).toFixed(0),
            good: +((counts.good / total) * 100).toFixed(0),
          };

          setPercentages(percentagesCalc);
        } else {
          const percentagesCalc = {
            low: +(0).toFixed(0),
            mild: +(0).toFixed(0),
            good: +(0).toFixed(0),
          };

          setPercentages(percentagesCalc);
        }

        setLoading("false");
      } catch (error) {
        message.error("Failed to fetch fuel data");
        console.log(error, "Failed to fetch fuel data");
      }
      setLoading(false);
    };

    fetchFuelData();
  }, [to, from, switchValue]);

  const chartKey = useMemo(() => {
    return JSON.stringify(selectedKey);
  }, [selectedKey]);

  return (
    <div className="fuel">
      <svg width={0} height={0}>
        <clipPath id="wave" clipPathUnits="objectBoundingBox">
          <path
            className="st0"
            d="M 0.995 0.067 c 0 0 -0.144 -0.091 -0.413 0.017 S 0.25 0.191 -0.002 0.1 V 1.269 h 1.001 L 0.995 0.067 z"
          />
        </clipPath>
      </svg>

      {loading ? (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
          className="loader-container"
        >
          <Lottie
            animationData={reportGeneration}
            loop={true}
            style={{ width: 150, height: 150 }}
          />
        </div>
      ) : (
        <div className="rows-container">
          <div className="row tabs">
            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Total Fuel Tanks</span>
                <span className="value-box" style={{ color: "#1FC16B" }}>
                  {fuelData?.total_fuel_tanks}
                </span>
              </div>
            </div>

            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Total Drains</span>
                <span className="value-box" style={{ color: "#DFB400" }}>
                  {fuelData?.total_drains}
                </span>
              </div>
            </div>

            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Total Fillings</span>
                <span className="value-box" style={{ color: "#155DFC" }}>
                  {fuelData?.total_fillings}
                </span>
              </div>
            </div>

            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Filling Volume</span>
                <span className="value-box" style={{ color: "#B73AFF" }}>
                  {fuelData?.filling_volume} L
                </span>
              </div>
            </div>
          </div>

          <div className="row tabs">
            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Drain Volume</span>
                <span className="value-box" style={{ color: "#FB3748" }}>
                  {fuelData?.drain_volume} L
                </span>
              </div>
            </div>
            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Fuel Economy</span>
                <span className="value-box" style={{ color: "#1FC16B" }}>
                  {fuelData?.fuel_economy}
                </span>
              </div>
            </div>
            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Fuel Economy</span>

                <div className="value-legend-grid">
                  <div className="value-legend-col">
                    <span
                      className="value-box value-box1"
                      style={{ color: "#1FC16B" }}
                    >
                      {percentages?.good}%
                    </span>
                    <span className="legend-item">
                      <span
                        className="legend-color"
                        style={{ background: "#1FC16B" }}
                      ></span>
                      Good
                    </span>
                  </div>

                  <div className="value-legend-col">
                    <span
                      className="value-box value-box2"
                      style={{ color: "#DFB400" }}
                    >
                      {percentages?.mild}%
                    </span>
                    <span className="legend-item">
                      <span
                        className="legend-color"
                        style={{ background: "#DFB400" }}
                      ></span>
                      Mild
                    </span>
                  </div>

                  <div className="value-legend-col">
                    <span
                      className="value-box value-box3"
                      style={{ color: "#FB3748" }}
                    >
                      {percentages?.low}%
                    </span>
                    <span className="legend-item">
                      <span
                        className="legend-color"
                        style={{ background: "#FB3748" }}
                      ></span>
                      Low
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="graph">
              <LineChart
                graphData={{
                  labels: fuelData?.quantity_of_fuel.labels,
                  added: fuelData?.quantity_of_fuel.added,
                  used: fuelData?.quantity_of_fuel.used,
                }}
                lengthKeys={2}
                colourTheme={["#2662D9", "#FB3748"]}
                graphTitle="Quantity to Fuel Used/Added"
                selectTenByDefault={false}
                units={["L", "L"]}
                classNameCSS="quantity-to-use"
              />
            </div>
            <div className="graph">
              <LineChart
                graphData={fuelData?.last_volume_stats || {}}
                lengthKeys={2}
                colourTheme={["#1FC16B", "#FB3748"]}
                graphTitle="Last Volume Status"
                selectTenByDefault={false}
                units={["L", "L"]}
                classNameCSS="last-volume-status"
              />
            </div>
          </div>

          <div className="row">
            <div className="graph">
              <CurrentFuelLevels fuelLevels={fuelData?.current_fuel_level} />
            </div>
          </div>

          <div className="row">
            <div className="graph">
              {keys.length > 0 && (
                <Select
                  value={selectedKey}
                  style={{ width: "100%", marginBottom: "1rem" }}
                  onChange={(value) => setSelectedKey(value)}
                >
                  {keys.map((key) => (
                    <Option key={key} value={key}>
                      {key}
                    </Option>
                  ))}
                </Select>
              )}
              <BarChart
                key={chartKey}
                graphData={fuelData?.fuel_use_trend?.[selectedKey] || {}}
                lengthKeys={1}
                colourTheme={["#2662D9"]}
                graphTitle="Fuel Use Trend"
                selectTenByDefault={false}
                units={["L"]}
                classNameCSS="fuel-use-trend"
              />
            </div>

            <div className="graph">
              <DynamicBarChart
                graphData={fuelData?.fuel_economy_data || {}}
                lengthKeys={1}
                colourTheme={["#2662D9"]}
                graphTitle="Fuel Economy"
                selectTenByDefault={false}
                classNameCSS="fuel-economy"
                dynamicColor={true}
                units={["km/L"]}
              />
            </div>
          </div>

          <div className="row">
            <div className="graph">
              <BarChart
                graphData={fuelData?.fuel_removal_time || {}}
                lengthKeys={1}
                colourTheme={[" #FB3748"]}
                graphTitle="Fuel Removal by Time"
                selectTenByDefault={false}
                units={["L"]}
                classNameCSS="fuel-removal-by-time"
              />
            </div>
          </div>

          <div className="row">
            <div className="graph" style={{ marginTop: "18px" }}>
              <MapComponent
                name={carName}
                latitude={mapCoordinates?.latitude}
                longitude={mapCoordinates?.longitude}
              />
            </div>
            <div className="graph">
              <GenericTable
                dataSource={fuelData?.fuel_removal_by_location}
                columns={columnsRemove}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Fuel;
